import React, { useState } from 'react';
import { Box, Flex, Separator, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import {
  setFetchedTransportation,
  setTransportationForm
} from '../../../store/slices/transportation/transportationSlice';
import { findTransportation } from '../../../apollo/queries/findTransportation';
import { RootState } from '../../../store/store';
import { getTranslationByPath } from '../../../utils/translations';
import { addNotification } from '../../../store/slices/notification/notificationSlice';
import { MainContent } from '../../../layout/content/MainContent';
import { KeyboardWrapper } from '../../../components/keyboard/KeyboardWrapper';
import { connectTransports } from '../../../apollo/mutations/connectTransports';


export type UnloadingWithLoadingTemplateType = {
  transitionUrl: TransitionUrl
  noUnloadInputLabel: TranslationPath
  noLoadInputLabel: TranslationPath
  noInputPlaceholder: TranslationPath
  rsPinInputLabel: TranslationPath
  rsPinInputPlaceholder: TranslationPath
  notFoundText: TranslationPath
}

type Props = {
  page: Page<UnloadingWithLoadingTemplateType>
}


export const UnloadingWithLoadingTemplate = (props: Props): React.ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false);

  const language = useLanguage();
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)
  const transportationTypeId = useSelector((state: RootState) => state.transportation.transportationTypeId)

  const [rsId, setRsId] = useState<string>('');
  const [unloadId, setUnloadId] = useState<string>('')
  const [loadId, setLoadId] = useState<string>('')

  const onContinue = async () => {
    if (rsId.length === 0 && (loadId.length === 0 || unloadId.length === 0)) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.notFoundText) },
      ))
      return
    }

    setIsProcessing(true);

    const unloadingTypes = [1,2,4,5,7,9]

    if (rsId.length > 0) {
      const findTransportationResult = await findTransportation({
        value: rsId,
        findBy: 'rsPin',
        qtirLanguageId: language.qtirLanguageId,
        transportationTypeId: unloadingTypes,
      })

      const foundTransportation = findTransportationResult.data.findTransportation.transportation
      if (!foundTransportation) {
        dispatch(addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.notFoundText) },
        ))
        setIsProcessing(false);
        return
      }

      dispatch(setFetchedTransportation(foundTransportation))
      dispatch(setTransportationForm({
        ...transportationForm,
        registrationNumber: foundTransportation.registrationNumber,
        driverName: foundTransportation.driverName,
        phoneNumber: foundTransportation.phoneNumber,
      }))

      setIsProcessing(false);
      navigate(props.page.data.transitionUrl)
    }

    if (loadId.length > 0 && unloadId.length > 0) {
      const findLoadTransportationResult = await findTransportation({
        value: loadId,
        findBy: 'no',
        qtirLanguageId: language.qtirLanguageId,
        transportationTypeId: transportationTypeId ? transportationTypeId : undefined,
      })
      const findUnloadTransportationResult = await findTransportation({
        value: unloadId,
        findBy: 'no',
        qtirLanguageId: language.qtirLanguageId,
        transportationTypeId: transportationTypeId ? transportationTypeId : undefined,
      })

      const { transportation: foundLoadTransportation } = findLoadTransportationResult.data.findTransportation
      const { transportation: foundUnloadTransportation } = findUnloadTransportationResult.data.findTransportation
      if (!foundLoadTransportation || !foundUnloadTransportation) {
        dispatch(addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.notFoundText) },
        ))
        setIsProcessing(false);
        return
      }

      console.log(foundUnloadTransportation)

      await connectTransports({ id: foundLoadTransportation.id , prevTransportId: foundUnloadTransportation.id })

      dispatch(setFetchedTransportation(foundUnloadTransportation))
      dispatch(setTransportationForm({
        ...transportationForm,
        registrationNumber: foundUnloadTransportation.registrationNumber,
        driverName: foundUnloadTransportation.driverName,
        phoneNumber: foundUnloadTransportation.phoneNumber,
      }))

      setIsProcessing(false);
      navigate(props.page.data.transitionUrl)
    }
  }

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            {props.page.data.rsPinInputLabel && (
              <>
                <Box maxWidth="500px" className="w-full">
                  <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                    {getTranslationByPath(language.translations, props.page.data.rsPinInputLabel)}
                  </Label.Root>
                  <KeyboardWrapper layout="numeric" title={getTranslationByPath(language.translations, props.page.data.rsPinInputLabel)}>
                    <TextField.Root
                      style={{ height: '50px' }}
                      className="mt-3"
                      placeholder={getTranslationByPath(language.translations, props.page.data.rsPinInputPlaceholder)}
                      onChange={event => setRsId(event.target.value)} />
                  </KeyboardWrapper>
                </Box><Separator style={{ width: '100%', maxWidth: '500px', margin: '2rem 0' }} />
              </>
            )}
            <Box maxWidth="500px" className="w-full">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.noUnloadInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.noUnloadInputLabel)}>
                <TextField.Root
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.noInputPlaceholder)}
                  onChange={event => setUnloadId(event.target.value)}
                />
              </KeyboardWrapper>
            </Box>
            <Box maxWidth="500px" className="w-full">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.noLoadInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.noLoadInputLabel)}>
                <TextField.Root
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.noInputPlaceholder)}
                  onChange={event => setLoadId(event.target.value)}
                />
              </KeyboardWrapper>
            </Box>
          </Flex>
        </Flex>
      </MainContent>

      <Footer backUrl={props.page.footer?.back} onContinue={onContinue} disabledBackBtn={isProcessing} disabledContinueBtn={isProcessing} />
    </>
  )
}
